import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useRouter } from 'next/router'
import {
  pageArea,
  scrollArea,
  subscribeToAppScroll,
} from 'utilities/work-time-scroll'
import * as R from 'ramda'

import useCurrentUser from 'hooks/use-current-user'
import { parseDateTime } from 'utilities/dates'
import useInterval from 'hooks/use-interval'
import {
  recordCoachWorkHeartbeat as recordCoachWorkHeartbeatMutation,
  recordCoachWorkDiagnostic as recordCoachWorkDiagnosticMutation,
} from './mutations.graphql'
import {
  getWorkTimeDiagnostics,
  readWorkDiagnosticsUntil,
  saveWorkDiagnosticsUntil,
  WorkTimeDiagnostics,
  DiagnosticState,
} from './diagnostics'
import { myCoachWorkTime as myCoachWorkTimeQuery } from './queries.graphql'
import {
  HEARTBEAT_INTERVAL_MS,
  INTERACTION_THROTTLE_MS,
  TICK_MS,
  ACTIVITY_EVENTS,
  isActive,
  isCrediting,
  nextCreditState,
  isOverWarnThreshold,
} from './logic'

interface CoachWorkTimeContextValue {
  // Only dietitian coaches see the dashboard indicator.
  showIndicator: boolean
  // Whether time is accruing right now (drives the live status dot): the coach
  // is locally active AND the server is still crediting (grey during a call).
  isCounting: boolean
  // Whether the current client page is excluded from tracked work time.
  isExcludedClient: boolean
  // Server-authoritative week-to-date seconds (updated on each heartbeat ack
  // and on query refetch). The UI shows minute granularity; the server is the
  // clock — we never tick this locally.
  displaySeconds: number
  // Weekly non-call budget (20% of last week's call time; 0 if none).
  weeklyBudgetSeconds: number
  // True once tracked time reaches 45% of last week's call time.
  isOverBudgetWarning: boolean
}

const DEFAULT_VALUE: CoachWorkTimeContextValue = {
  showIndicator: false,
  isCounting: false,
  isExcludedClient: false,
  displaySeconds: 0,
  weeklyBudgetSeconds: 0,
  isOverBudgetWarning: false,
}

const CoachWorkTimeContext =
  createContext<CoachWorkTimeContextValue>(DEFAULT_VALUE)

export const useCoachWorkTime = () => useContext(CoachWorkTimeContext)

export const CoachWorkTimeProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const router = useRouter()
  const currentUser = useCurrentUser()
  const coach = currentUser?.data?.me?.user?.coach
  const coachId = coach?.id

  // Continue tracking every coach; show the indicator only to dietitians.
  const onDashboard = router.pathname.startsWith('/dashboard')
  const enabled = !!coachId && onDashboard
  const showIndicator = enabled && !!coach?.isInsuranceDietitian

  // CLIENT context when on a client-scoped page, else OTHER.
  const clientId = (router.query.clientId as string) || null

  const [recordHeartbeat] = useMutation(recordCoachWorkHeartbeatMutation)

  const [recordDiagnostic] = useMutation(recordCoachWorkDiagnosticMutation)
  const diagnosticsRef = useRef<WorkTimeDiagnostics | null>(null)
  const diagnosticsUntilRef = useRef(0)
  const diagnosticStateRef = useRef<DiagnosticState | null>(null)
  const wasEnabledRef = useRef(false)

  // Fetch the initial total and budget only for coaches who see the readout.
  const { data: workTimeData } = useQuery(myCoachWorkTimeQuery, {
    skip: !showIndicator,
    fetchPolicy: 'cache-and-network',
  })
  const weeklyBudgetSeconds =
    workTimeData?.myCoachWorkTime?.weeklyBudgetSeconds ?? 0

  const [isCounting, setIsCounting] = useState(false)
  const [excludedClientId, setExcludedClientId] = useState<string | null>(null)
  const [displaySeconds, setDisplaySeconds] = useState(0)

  // Refs so the interval/event callbacks always read fresh values without
  // resubscribing.
  const lastInteractionRef = useRef(Date.now())
  const lastThrottleRef = useRef(0)
  const isVisibleRef = useRef(true)
  const clientIdRef = useRef(clientId)
  const enabledRef = useRef(enabled)
  const pathnameRef = useRef(router.pathname)
  // A new object fences pending requests even across A -> B -> A switches.
  // Invalidate during render, before old listeners/effects are cleaned up.
  const coachSessionRef = useRef({ coachId })
  if (coachSessionRef.current.coachId !== coachId)
    coachSessionRef.current = { coachId }
  const coachSession = coachSessionRef.current
  clientIdRef.current = clientId
  enabledRef.current = enabled
  pathnameRef.current = router.pathname
  // High-water mark of the server week-total we've seen, and when a *credited*
  // heartbeat last raised it. Drives the dot (see isCreditingNow). Initialised
  // to 0 so the dot stays grey until a real credited beat — no green flash on
  // mount, and a seed/refetch that raises the total doesn't green it either.
  const lastServerSecondsRef = useRef(0)
  const lastCreditedAtRef = useRef(0)
  const nextHeartbeatIdRef = useRef(0)
  const lastAppliedHeartbeatIdRef = useRef(0)
  const contextVersionRef = useRef(0)

  // Locally active = visible tab + a real action within the idle window. Drives
  // whether we *send* beats; the dot additionally requires the server to be
  // crediting (below).
  const isLocallyActive = () =>
    isActive({
      visible: isVisibleRef.current,
      lastInteractionAt: lastInteractionRef.current,
      now: Date.now(),
    })

  const isCreditingNow = () =>
    isCrediting({ creditedAt: lastCreditedAtRef.current, now: Date.now() })

  // The readout is server-authoritative: show the latest week total from the
  // server (heartbeat ack or query refetch) — never a locally-ticked estimate,
  // so it can't drift ahead or jump backward. Only a heartbeat *ack* that
  // raises the total (`fromCredit`) advances the "credited" marker that greens
  // the dot; a seed/refetch can raise the total without the server crediting
  // right now (e.g. returning to a tab during a call), so it updates the
  // readout only.
  const applyServerSeconds = useCallback(
    (seconds: number, fromCredit = false) => {
      const next = nextCreditState({
        prevSeenSeconds: lastServerSecondsRef.current,
        prevCreditedAt: lastCreditedAtRef.current,
        seconds,
        fromCredit,
        now: Date.now(),
      })
      lastServerSecondsRef.current = next.seenSeconds
      lastCreditedAtRef.current = next.creditedAt
      setDisplaySeconds(seconds)
    },
    []
  )

  // Seed / refresh from the server's week-to-date (also refetched on tab
  // refocus, so a coach returning to an idle tab sees the correct total).
  useEffect(() => {
    const tracked = workTimeData?.myCoachWorkTime?.trackedSeconds
    if (typeof tracked === 'number') applyServerSeconds(tracked)
  }, [workTimeData, applyServerSeconds])

  const diagnosticSnapshot = () => {
    if (coachSessionRef.current !== coachSession) return undefined
    if (Date.now() >= diagnosticsUntilRef.current) return undefined
    try {
      return diagnosticsRef.current?.snapshot(
        Date.now(),
        lastInteractionRef.current,
        isVisibleRef.current,
        document.hasFocus(),
        pageArea(pathnameRef.current)
      )
    } catch {
      // Evidence is optional; even a browser API failure must not skip a beat.
      return undefined
    }
  }

  const reportDiagnosticState = (forcedState?: DiagnosticState) => {
    if (coachSessionRef.current !== coachSession) return
    if (Date.now() >= diagnosticsUntilRef.current) {
      diagnosticStateRef.current = null
      return
    }
    const state =
      forcedState ??
      (!enabledRef.current
        ? 'DISABLED'
        : !isVisibleRef.current
        ? 'HIDDEN'
        : isLocallyActive()
        ? 'ACTIVE'
        : 'IDLE')
    if (diagnosticStateRef.current === state) return
    const diagnostics = diagnosticSnapshot()
    if (!diagnostics) return
    diagnosticStateRef.current = state
    // Best effort. Transitions never call the crediting mutation, and failures
    // don't trigger an unbounded retry loop or change activity/credit state.
    recordDiagnostic({ variables: { state, diagnostics } }).catch(() => {})
  }

  const fireHeartbeat = () => {
    if (
      coachSessionRef.current !== coachSession ||
      !enabledRef.current ||
      !isLocallyActive()
    )
      return
    const cid = clientIdRef.current
    const heartbeatId = ++nextHeartbeatIdRef.current
    const contextVersion = contextVersionRef.current
    recordHeartbeat({
      variables: {
        contextType: cid ? 'CLIENT' : 'OTHER',
        clientId: cid,
        diagnostics: diagnosticSnapshot(),
      },
    })
      .then(res => {
        if (coachSessionRef.current !== coachSession) return
        // Overlapping beats can finish out of order. Never let an older
        // acknowledgement undo a newer client-status decision or week total.
        if (heartbeatId <= lastAppliedHeartbeatIdRef.current) return
        lastAppliedHeartbeatIdRef.current = heartbeatId
        const until = res.data?.recordCoachWorkHeartbeat?.diagnosticsUntil
        const cutoff = until == null ? 0 : parseDateTime(until).getTime()
        diagnosticsUntilRef.current = Number.isFinite(cutoff) ? cutoff : 0
        diagnosticsRef.current?.setCollectionUntil(diagnosticsUntilRef.current)
        saveWorkDiagnosticsUntil(String(coachId), diagnosticsUntilRef.current)
        reportDiagnosticState()
        // The server is the clock: snap the readout to its authoritative week
        // total.
        const weekSeconds = R.path(
          ['data', 'recordCoachWorkHeartbeat', 'weekTrackedSeconds'],
          res
        )
        const canTrackContext = R.path(
          ['data', 'recordCoachWorkHeartbeat', 'canTrackContext'],
          res
        )
        // A previous client's in-flight beat cannot change the current dot.
        const isCurrentContext =
          contextVersionRef.current === contextVersion &&
          clientIdRef.current === cid
        if (typeof weekSeconds === 'number')
          applyServerSeconds(
            weekSeconds,
            isCurrentContext && canTrackContext === true
          )
        if (isCurrentContext && typeof canTrackContext === 'boolean') {
          setExcludedClientId(canTrackContext ? null : cid)
          if (!canTrackContext) {
            lastCreditedAtRef.current = 0
            setIsCounting(false)
          }
        }
      })
      .catch(() => {
        if (coachSessionRef.current !== coachSession) return
        diagnosticsRef.current?.heartbeatFailed()
        // A dropped heartbeat is non-fatal: it just misses one ~25s slice, and
        // the next beat resumes crediting.
      })
  }

  // Activity + visibility listeners (client-only, while enabled).
  useEffect(() => {
    const startsActivityWindow = enabled && !wasEnabledRef.current
    wasEnabledRef.current = enabled
    diagnosticStateRef.current = null
    diagnosticsRef.current = null
    diagnosticsUntilRef.current = 0
    if (!enabled || typeof window === 'undefined') return

    diagnosticsRef.current = getWorkTimeDiagnostics(String(coachId))
    diagnosticsUntilRef.current = readWorkDiagnosticsUntil(String(coachId))
    diagnosticsRef.current.setCollectionUntil(diagnosticsUntilRef.current)
    const unsubscribeScroll = subscribeToAppScroll(
      diagnosticsRef.current.observeAppScroll
    )
    const onPointer = (event: PointerEvent) => {
      diagnosticsRef.current?.observePointer(
        event.type,
        event.isTrusted,
        event.buttons,
        Date.now()
      )
    }
    const onInteraction = (event: Event) => {
      const now = Date.now()
      const accepted = now - lastThrottleRef.current >= INTERACTION_THROTTLE_MS
      diagnosticsRef.current?.observe(
        event.type,
        event.isTrusted,
        accepted,
        now
      )
      if (event.type === 'scroll' && now < diagnosticsUntilRef.current) {
        // A failing optional DOM classification must not alter activity tracking.
        try {
          diagnosticsRef.current?.observeScrollArea(scrollArea(event.target))
        } catch {}
      }
      if (accepted) {
        lastThrottleRef.current = now
        lastInteractionRef.current = now
      }
    }
    const onTouch = (event: Event) => {
      diagnosticsRef.current?.observe(
        event.type,
        event.isTrusted,
        false,
        Date.now()
      )
    }
    const onVisibility = () => {
      // Visibility only gates counting; coming back into view is not itself a
      // work action, so we don't reset the activity timer here.
      isVisibleRef.current = document.visibilityState === 'visible'
      reportDiagnosticState()
    }

    const opts: AddEventListenerOptions = { passive: true, capture: true }
    ACTIVITY_EVENTS.forEach(evt =>
      window.addEventListener(evt, onInteraction, opts)
    )
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('touchstart', onTouch, opts)
    window.addEventListener('pointerdown', onPointer, opts)
    window.addEventListener('pointermove', onPointer, opts)

    isVisibleRef.current = document.visibilityState === 'visible'
    // Opening the dashboard starts a window. A coach-only change preserves
    // the existing activity clock; diagnostics must not create paid activity.
    if (startsActivityWindow) lastInteractionRef.current = Date.now()
    diagnosticsRef.current.initialize(Date.now())

    return () => {
      ACTIVITY_EVENTS.forEach(evt =>
        window.removeEventListener(evt, onInteraction, opts)
      )
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('touchstart', onTouch, opts)
      window.removeEventListener('pointerdown', onPointer, opts)
      window.removeEventListener('pointermove', onPointer, opts)
      unsubscribeScroll()
      // Never submit the previous coach's trace under the new authentication.
      if (coachSessionRef.current === coachSession)
        reportDiagnosticState('DISABLED')
    }
    // Callbacks use refs; diagnostics must not reset the activity window.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, coachId])

  // Recompute the dot: green only while locally active AND the server is still
  // crediting. Handles the idle timeout, tab hide, and live-call greying with no
  // explicit event. The readout itself is server-driven, so it's untouched here.
  useInterval(
    () => {
      reportDiagnosticState()
      const counting = isLocallyActive() && isCreditingNow()
      setIsCounting(prev => (prev === counting ? prev : counting))
    },
    enabled ? TICK_MS : null
  )

  // Heartbeat cadence.
  useInterval(fireHeartbeat, enabled ? HEARTBEAT_INTERVAL_MS : null)

  // Immediate heartbeat on enable and on context (client) switch, so the switch
  // is attributed to the new client right away rather than on the next ~25s tick.
  useEffect(() => {
    contextVersionRef.current += 1
    if (!enabled) {
      setIsCounting(false)
      return
    }
    // A new context needs its own server acknowledgement before the dot can
    // become green. This also greys it while checking an inactive client.
    lastCreditedAtRef.current = 0
    setIsCounting(false)
    fireHeartbeat()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, clientId])

  return (
    <CoachWorkTimeContext.Provider
      value={{
        showIndicator,
        isCounting,
        isExcludedClient: clientId !== null && excludedClientId === clientId,
        displaySeconds,
        weeklyBudgetSeconds,
        isOverBudgetWarning: isOverWarnThreshold({
          displaySeconds,
          weeklyBudgetSeconds,
        }),
      }}>
      {children}
    </CoachWorkTimeContext.Provider>
  )
}

export default CoachWorkTimeProvider
